import { create } from "zustand";
import { toast } from "sonner";
import {
  getUnreadCount,
  listNotifications,
  markNotificationsRead,
  markAllNotificationsRead,
  type NotificationResponse,
} from "@/api/client";

interface NotificationState {
  unreadCount: number;
  notifications: NotificationResponse[];
  isOpen: boolean;
  isLoading: boolean;
  fetchUnreadCount: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
  markAllRead: () => Promise<void>;
  toggle: () => void;
  close: () => void;
}

// Guard: skip polling/fetch while a mark operation is in-flight
let markInFlight = 0;
let workspaceGeneration = 0;

/** Ignore late responses when entering or leaving the isolated demo. */
export function clearNotificationWorkspace() {
  workspaceGeneration++;
  soundPrimed = false;
  useNotificationStore.setState({ unreadCount: 0, notifications: [], isOpen: false, isLoading: false });
}

export const NOTIFICATION_SOUND_KEY = "trace-notification-sound";

export function notificationSoundEnabled(): boolean {
  try {
    return window.localStorage.getItem(NOTIFICATION_SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

// First unread-count fetch after load must stay silent — chiming for
// notifications that piled up while the app was closed reads as a bug.
let soundPrimed = false;

/** Distinctive two-tone chime for a NEW in-app notification (WebAudio, no
 * asset). OS-level web-push notifications keep the system sound — browsers
 * don't allow custom sounds there. Fails silently before any user gesture
 * (autoplay policy). */
function playNotificationChime() {
  try {
    type AudioCtor = typeof AudioContext;
    const Ctx: AudioCtor | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioCtor })
        .webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    for (const [freq, at] of [
      [880, 0],
      [1318.5, 0.12],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + at);
      osc.connect(gain);
      osc.start(now + at);
      osc.stop(now + at + 0.4);
    }
    window.setTimeout(() => void ctx.close().catch(() => undefined), 700);
  } catch {
    // Autoplay blocked or no audio device — the badge still updates.
  }
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  unreadCount: 0,
  notifications: [],
  isOpen: false,
  isLoading: false,

  fetchUnreadCount: async () => {
    const generation = workspaceGeneration;
    if (markInFlight > 0) return;
    try {
      const data = await getUnreadCount();
      if (generation !== workspaceGeneration) return;
      if (markInFlight > 0) return;
      const prev = get().unreadCount;
      if (
        soundPrimed &&
        data.unread_count > prev &&
        notificationSoundEnabled()
      ) {
        playNotificationChime();
      }
      soundPrimed = true;
      set({ unreadCount: data.unread_count });
    } catch {
      // silently fail
    }
  },

  fetchNotifications: async () => {
    const generation = workspaceGeneration;
    set({ isLoading: true });
    try {
      const data = await listNotifications({ limit: 50 });
      if (generation !== workspaceGeneration) return;
      if (markInFlight > 0) {
        set({ notifications: data.notifications, isLoading: false });
        return;
      }
      set({
        notifications: data.notifications,
        unreadCount: data.unread_count,
        isLoading: false,
      });
    } catch {
      if (generation === workspaceGeneration) set({ isLoading: false });
    }
  },

  markRead: async (ids: string[]) => {
    const generation = workspaceGeneration;
    const prev = get();
    markInFlight++;
    set({
      notifications: prev.notifications.map((n) =>
        ids.includes(n.id) ? { ...n, is_read: true } : n,
      ),
      unreadCount: Math.max(0, prev.unreadCount - ids.length),
    });
    try {
      await markNotificationsRead(ids);
    } catch (err) {
      if (generation !== workspaceGeneration) return;
      console.error("[notifications] markRead failed:", err);
      toast.error(`Mark read failed: ${err instanceof Error ? err.message : "Unknown error"}`);
      set({ notifications: prev.notifications, unreadCount: prev.unreadCount });
    } finally {
      markInFlight--;
    }
  },

  markAllRead: async () => {
    const generation = workspaceGeneration;
    const prev = get();
    markInFlight++;
    set({
      notifications: prev.notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    });
    try {
      await markAllNotificationsRead();
    } catch (err) {
      if (generation !== workspaceGeneration) return;
      console.error("[notifications] markAllRead failed:", err);
      toast.error(`Mark all read failed: ${err instanceof Error ? err.message : "Unknown error"}`);
      set({ notifications: prev.notifications, unreadCount: prev.unreadCount });
    } finally {
      markInFlight--;
    }
  },

  toggle: () => {
    const isOpen = !get().isOpen;
    set({ isOpen });
    if (isOpen) {
      get().fetchNotifications();
    }
  },

  close: () => set({ isOpen: false }),
}));
